import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  enUS as muiCore_enUS,
  Localization as MuiCoreLocalization,
  nbNO as muiCore_nbNO,
} from "@mui/material/locale";
import {
  enUS as dataGrid_enUS,
  nbNO as dataGrid_nbNO,
} from "@mui/x-data-grid-premium/locales";
import {
  LocalizationProvider as DatePickerLocalizationProvider,
  PickersLocaleText,
} from "@mui/x-date-pickers-pro";
import { nbNO as dp_nbNO } from "@mui/x-date-pickers-pro/locales";
import { IllegalArgumentError } from "shared-ts-utils-core";
import { merge } from "lodash";
import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon";
import { GridLocaleText } from "@mui/x-data-grid-premium";
import { ChartsLocalizationProvider } from "@mui/x-charts-premium";
import { ChartsLocaleText } from "@mui/x-charts/locales";
import { enUSLocaleText as charts_en } from "@mui/x-charts/locales";
import type { SchedulerLocalization } from "@mui/x-scheduler-premium/locales";
import { scheduler_enUS, scheduler_nbNO } from "./schedulerLocales";
export interface MuiDataGridLocalization {
  components: {
    MuiDataGrid: {
      defaultProps: {
        localeText: Partial<GridLocaleText>;
      };
    };
  };
}

export interface MuiDataPickersLocalization {
  components: {
    MuiLocalizationProvider: {
      defaultProps: {
        localeText: Partial<PickersLocaleText>;
      };
    };
  };
}

export interface MuiChartsLocalization {
  components: {
    MuiCharts: {
      defaultProps: {
        localeText: Partial<ChartsLocaleText>;
      };
    };
  };
}

export type MuiLocalization = MuiCoreLocalization &
  MuiDataGridLocalization &
  MuiChartsLocalization &
  SchedulerLocalization;

const mui_enUS: MuiLocalization = merge(
  muiCore_enUS,
  dataGrid_enUS,
  scheduler_enUS
) as MuiLocalization;

const mui_nbNo: MuiLocalization = merge(
  muiCore_nbNO,
  dataGrid_nbNO,
  scheduler_nbNO
) as MuiLocalization;

const localizationByLanguage: Record<string, MuiLocalization> = {
  en: mui_enUS,
  no: mui_nbNo,
  "en-US": mui_enUS,
  "en-GB": mui_enUS,
  "nb-NO": mui_nbNo,
  "nn-NO": mui_nbNo,
};

const charts_no: Partial<ChartsLocaleText> = {
  noData: "Ingen data",
  loading: "Laster data",
  zoomIn: "Forstørr",
  zoomOut: "Forminsk",
};

const chartsLocaleText_by_language: Record<
  string,
  Partial<ChartsLocaleText>
> = {
  en: charts_en,
  no: charts_no,
  "en-US": charts_en,
  "en-GB": charts_en,
  "nb-NO": charts_no,
  "nn-NO": charts_no,
};

interface MuiLocalizationContextType {
  muiLocalization: MuiLocalization;
  setLocale: (language: string) => void;
}

const MuiLocalizationContext = createContext<MuiLocalizationContextType>({
  muiLocalization: mui_enUS,
  setLocale: () => {
    /* Nothing to do */
  },
});

export interface MuiLocalizationProviderProps {
  language: string;
  children: ReactNode;
}

export const MuiLocalizationProvider: FC<MuiLocalizationProviderProps> = (
  props
) => {
  const [muiLocalization, setMuiLocalization] = useState(
    resolveLocalization(props.language)
  );
  const setLocale = useCallback((language: string) => {
    setMuiLocalization(resolveLocalization(language));
  }, []);

  useEffect(() => {
    setMuiLocalization(resolveLocalization(props.language));
  }, [props.language]);

  const chartsLocalText = chartsLocaleText_by_language[props.language];

  const contextValue: MuiLocalizationContextType = useMemo(() => {
    return {
      muiLocalization: muiLocalization,
      setLocale: setLocale,
    };
  }, [muiLocalization, setLocale]);
  return (
    <MuiLocalizationContext.Provider value={contextValue}>
      <DatePickerLocalizationProvider
        dateAdapter={AdapterLuxon}
        adapterLocale={props.language}
        localeText={
          dp_nbNO.components.MuiLocalizationProvider.defaultProps.localeText
        }
      >
        <ChartsLocalizationProvider localeText={chartsLocalText}>
          {props.children}
        </ChartsLocalizationProvider>
      </DatePickerLocalizationProvider>
    </MuiLocalizationContext.Provider>
  );
};

export const useMuiLocalization = (): MuiLocalizationContextType => {
  return useContext(MuiLocalizationContext);
};

const resolveLocalization = (language: string): MuiLocalization => {
  const localization = localizationByLanguage[language];
  if (localization == null) {
    throw new IllegalArgumentError("Language not supported: " + language);
  }
  return localization;
};
